import React, { useState, useMemo } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import styled from "@emotion/styled";
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  VStack,
  HStack,
  Badge,
  Select,
  Spinner,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Tooltip,
  Avatar,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { MdRefresh, MdCode } from "react-icons/md";
import { FaTowerBroadcast } from "react-icons/fa6";
import { motion } from "framer-motion";
import Card from "components/card/Card";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyUAdmins } from "utils/accessLevels";

const GlassMorphismCard = styled(Card)`
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }
`;

const MotionTr = motion(Tr);

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => (
  <Button
    size="sm"
    variant={isActive ? "solid" : "outline"}
    colorScheme={isActive ? "brand" : "gray"}
    isDisabled={isDisabled}
    onClick={onClick}
    minW="40px"
    h="40px"
    borderRadius="md"
    _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
    transition="all 0.2s"
  >
    {children}
  </Button>
);

const locationColumns = [
  { Header: "S.No" },
  { Header: "Name" },
  { Header: "City" },
  { Header: "State" },
  { Header: "ID" },
  { Header: "Actions" },
];

const OcpiLocationsTable = ({
  locations,
  refetchLocations,
  locListLoading,
  handleBroadcastAll,
  allLocLoading,
  handleBroadcastRow,
  handleViewBody,
  rowBroadcasting,
}) => {
  const [locationSearch, setLocationSearch] = useState("");

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const locColumns = useMemo(() => locationColumns, []);
  const locData = useMemo(() => locations, [locations]);

  const locTableInstance = useTable(
    {
      columns: locColumns,
      data: locData,
      initialState: { pageIndex: 0, pageSize: 10 },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") return rows;
        const q = query.toLowerCase().trim();
        return rows.filter((row) => {
          const o = row.original;
          return (
            (o.Name || "").toLowerCase().includes(q) ||
            (o.City || "").toLowerCase().includes(q) ||
            (o.State || "").toLowerCase().includes(q) ||
            String(o.Id ?? "").includes(q)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const handleLocSearchChange = (e) => {
    const v = e.target.value;
    setLocationSearch(v);
    locTableInstance.setGlobalFilter(v);
  };

  const renderPagination = () => {
    const {
      page,
      state: { pageIndex, pageSize },
      canPreviousPage,
      canNextPage,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize: setPS,
    } = locTableInstance;

    return (
      <Flex
        justify="space-between"
        align="center"
        px={6}
        py={4}
        borderTop="1px solid"
        borderColor={borderColor}
        flexWrap="wrap"
        gap={3}
      >
        <HStack spacing={3}>
          <Select
            size="sm"
            value={pageSize}
            onChange={(e) => setPS(Number(e.target.value))}
            borderRadius="md"
            w="120px"
          >
            {[5, 10, 20, 30, 50].map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Select>
          <Text fontSize="sm" color="gray.500">
            Showing {page.length} of {locations.length} results
          </Text>
        </HStack>

        <HStack spacing={2}>
          <PaginationButton isDisabled={!canPreviousPage} onClick={() => gotoPage(0)}>
            <ChevronLeftIcon boxSize={4} mr={1} />
            First
          </PaginationButton>
          <PaginationButton isDisabled={!canPreviousPage} onClick={previousPage}>
            <ChevronLeftIcon boxSize={4} />
          </PaginationButton>

          {pageCount <= 5 ? (
            Array.from({ length: pageCount }, (_, i) => (
              <PaginationButton key={i} isActive={i === pageIndex} onClick={() => gotoPage(i)}>
                {i + 1}
              </PaginationButton>
            ))
          ) : (
            <>
              {pageIndex > 1 && (
                <PaginationButton onClick={() => gotoPage(0)}>1</PaginationButton>
              )}
              {pageIndex > 2 && <Text color="gray.400">...</Text>}
              {pageIndex > 0 && (
                <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                  {pageIndex}
                </PaginationButton>
              )}
              <PaginationButton isActive>{pageIndex + 1}</PaginationButton>
              {pageIndex < pageCount - 1 && (
                <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                  {pageIndex + 2}
                </PaginationButton>
              )}
              {pageIndex < pageCount - 3 && <Text color="gray.400">...</Text>}
              {pageIndex < pageCount - 2 && (
                <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                  {pageCount}
                </PaginationButton>
              )}
            </>
          )}

          <PaginationButton isDisabled={!canNextPage} onClick={nextPage}>
            <ChevronRightIcon boxSize={4} />
          </PaginationButton>
          <PaginationButton isDisabled={!canNextPage} onClick={() => gotoPage(pageCount - 1)}>
            Last
            <ChevronRightIcon boxSize={4} ml={1} />
          </PaginationButton>
        </HStack>
      </Flex>
    );
  };

  return (
    <GlassMorphismCard>
      <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={4}
        >
          <VStack align="start" spacing={2} width={{ base: "100%", md: "auto" }}>
            <Text fontSize="xs" fontWeight="medium" color="gray.500">
              Search Locations
            </Text>
            <HStack spacing={10}>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by name, city, state, ID..."
                  value={locationSearch}
                  onChange={handleLocSearchChange}
                  borderRadius="md"
                  width="350px"
                  _focus={{
                    borderColor: "brand.500",
                    boxShadow: "0 0 0 1px #23A979",
                  }}
                />
              </InputGroup>
              <Button
                leftIcon={<Icon as={MdRefresh} />}
                size="sm"
                borderRadius="md"
                variant="ghost"
                color="brand.500"
                onClick={() => refetchLocations()}
                isLoading={locListLoading}
                loadingText="Refreshing..."
                aria-label="Refresh locations"
                fontWeight="500"
              >
                Refresh
              </Button>
              <RoleBasedRender roles={onlyUAdmins}>
                <Button
                  leftIcon={<Icon as={FaTowerBroadcast} boxSize={4} />}
                  onClick={() => handleBroadcastAll("locations")}
                  isLoading={allLocLoading}
                  loadingText="Broadcasting..."
                  size="sm"
                  height="36px"
                  minW="160px"
                  px={6}
                  bg="brand.500"
                  color="white"
                  borderRadius="8px"
                  boxShadow="0 4px 12px rgba(35, 169, 121, 0.3)"
                  fontWeight="600"
                  transition="all 0.2s ease"
                  _hover={{
                    bg: "brand.600",
                    boxShadow: "0 6px 16px rgba(35, 169, 121, 0.4)",
                    transform: "translateY(-2px)",
                  }}
                  _active={{
                    transform: "translateY(0)",
                    boxShadow: "0 2px 8px rgba(35, 169, 121, 0.3)",
                  }}
                >
                  Broadcast All
                </Button>
              </RoleBasedRender>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
              <Icon as={InfoIcon} boxSize={3} mr={1} />
              Type any keyword to search across all fields
            </Text>
          </VStack>

          <Flex align="center" justify="flex-end">
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Locations
            </Text>
            <Badge ml={2} colorScheme="brand" borderRadius="full" px={2}>
              {locations.length} total
            </Badge>
          </Flex>
        </Flex>
      </Box>

      {locListLoading ? (
        <Flex justify="center" py={10}>
          <Spinner color="brand.500" size="lg" />
        </Flex>
      ) : locations.length === 0 ? (
        <Flex justify="center" py={10}>
          <Text color="gray.400" fontSize="sm">No locations found.</Text>
        </Flex>
      ) : (
        <>
          <TableContainer>
            <Table {...locTableInstance.getTableProps()} variant="simple" colorScheme="gray">
              <Thead bg={theadBgColor}>
                {locTableInstance.headerGroups.map((hg, hgIdx) => (
                  <Tr {...hg.getHeaderGroupProps()} key={hgIdx}>
                    {hg.headers.map((column, colIdx) => (
                      <Th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        key={colIdx}
                        py={4}
                        px={6}
                        borderColor={borderColor}
                        textTransform="uppercase"
                        fontSize="xs"
                        fontWeight="bold"
                        letterSpacing="wider"
                        color="gray.500"
                        textAlign="center"
                        cursor="pointer"
                        _hover={{ color: "brand.500" }}
                        transition="all 0.2s"
                      >
                        <Flex justify="center" align="center" gap={1}>
                          {column.render("Header")}
                          <Box>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <Icon as={FaSortDown} />
                              ) : (
                                <Icon as={FaSortUp} />
                              )
                            ) : (
                              <Icon as={FaSort} opacity={0.3} />
                            )}
                          </Box>
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody {...locTableInstance.getTableBodyProps()}>
                {locTableInstance.page.map((row, rowIdx) => {
                  locTableInstance.prepareRow(row);
                  const loc = row.original;
                  return (
                    <MotionTr
                      {...row.getRowProps()}
                      key={row.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: rowIdx * 0.03 }}
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                    >
                      {row.cells.map((cell, cellIdx) => {
                        let cellData;
                        if (cell.column.Header === "S.No") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Text color={textColor} fontSize="sm" fontWeight="600">
                                {locTableInstance.state.pageIndex * locTableInstance.state.pageSize + rowIdx + 1}
                              </Text>
                            </Flex>
                          );
                        } else if (cell.column.Header === "Name") {
                          const initials = (loc.Name || "")
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .substring(0, 2);
                          cellData = (
                            <Flex align="center" gap={3}>
                              <Avatar
                                name={loc.Name}
                                size="sm"
                                bg="brand.500"
                                color="white"
                                fontWeight="bold"
                                getInitials={() => initials}
                              />
                              <Box>
                                <Text color={textColor} fontWeight="semibold" fontSize="sm">
                                  {loc.Name || "—"}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {[loc.City, loc.State].filter(Boolean).join(", ") || "—"}
                                </Text>
                              </Box>
                            </Flex>
                          );
                        } else if (cell.column.Header === "City") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Text color={textColor} fontSize="sm">{loc.City || "—"}</Text>
                            </Flex>
                          );
                        } else if (cell.column.Header === "State") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Text color={textColor} fontSize="sm">{loc.State || "—"}</Text>
                            </Flex>
                          );
                        } else if (cell.column.Header === "ID") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Badge
                                bg="brand.500"
                                color="white"
                                borderRadius="full"
                                px={3}
                                py={1}
                                fontWeight="bold"
                              >
                                {loc.Id}
                              </Badge>
                            </Flex>
                          );
                        } else if (cell.column.Header === "Actions") {
                          cellData = (
                            <Flex align="center" justify="center" gap={2}>
                              <Tooltip label="View OCPI body" placement="top" hasArrow>
                                <IconButton
                                  icon={<Icon as={MdCode} />}
                                  colorScheme="blue"
                                  variant="ghost"
                                  size="sm"
                                  borderRadius="full"
                                  aria-label="View OCPI body"
                                  onClick={() =>
                                    handleViewBody("location", loc.Id, loc.Name || `Location ${loc.Id}`)
                                  }
                                />
                              </Tooltip>
                              <RoleBasedRender roles={onlyUAdmins}>
                                <Tooltip label="Broadcast to OCPI peers" placement="top" hasArrow>
                                  <IconButton
                                    icon={
                                      rowBroadcasting.has(`location-${loc.Id}`) ? (
                                        <Spinner size="xs" />
                                      ) : (
                                        <Icon as={FaTowerBroadcast} />
                                      )
                                    }
                                    size="sm"
                                    borderRadius="full"
                                    bg="brand.500"
                                    color="white"
                                    _hover={{
                                      bg: "brand.600",
                                      transform: "translateY(-1px)",
                                    }}
                                    aria-label="Broadcast location"
                                    isDisabled={rowBroadcasting.has(`location-${loc.Id}`)}
                                    onClick={() => handleBroadcastRow("location", loc.Id)}
                                  />
                                </Tooltip>
                              </RoleBasedRender>
                            </Flex>
                          );
                        }
                        return (
                          <Td
                            {...cell.getCellProps()}
                            key={cellIdx}
                            py={4}
                            px={6}
                            borderColor={borderColor}
                            fontSize="sm"
                          >
                            {cellData}
                          </Td>
                        );
                      })}
                    </MotionTr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
          {renderPagination()}
        </>
      )}
    </GlassMorphismCard>
  );
};

export default OcpiLocationsTable;
